import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './chatbot.css';
import apiService from '../../services/api';

const ChatBot = () => {
  const [showChat, setShowChat] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'Hello! How can I assist you today?', id: Date.now() }
  ]);
  const [loading, setLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [hasQueried, setHasQueried] = useState(false);
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const predefinedQuestions = [
    "How many projects are there?",
    "What are the Different Roles in CSAT?",
    "Which Project has Highest CSAT Score?",
    "Which Project has Lowest CSAT Score?",
    "How many responses received?",
    "How many surveys sent?"
  ];

  const chatEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const chatIconUrl = apiService.getBlobAssetUrl('chatbot-icon 1.svg');

  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        setQuestion(finalTranscript || interimTranscript);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        setMessages((prev) => [
          ...prev,
          { from: 'bot', text: 'Sorry, I could not understand the audio.', id: Date.now() }
        ]);
      };
    } else {
      console.error('Speech recognition not supported in this browser.');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!showChat && recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setQuestion('');
      const timeoutId = setTimeout(() => {
        recognitionRef.current = null;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [showChat, isRecording]);

  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else if (recognitionRef.current) {
      setQuestion('');
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const speakMessage = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.volume = 1;
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => {
        setCurrentUtterance(null);
        setPlayingMessageId(null);
        setIsPaused(false);
      };
      setCurrentUtterance({ id, utterance });
      setPlayingMessageId(id);
      setIsPaused(false);
      window.speechSynthesis.speak(utterance);
    } else {
      console.error('Text-to-speech not supported in this browser.');
    }
  };

  const togglePlayPause = (text, id) => {
    if ('speechSynthesis' in window) {
      if (playingMessageId === id && window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.pause();
        setIsPaused(true);
      } else if (playingMessageId === id && window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else {
        speakMessage(text, id);
      }
    }
  };

  const stopMessage = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setCurrentUtterance(null);
      setPlayingMessageId(null);
      setIsPaused(false);
    }
  };

  const formatMessage = (text) => {
    marked.setOptions({
      gfm: true,
      breaks: true
    });
    let html = marked(text);
    html = DOMPurify.sanitize(html);
    html = html.replace(/<table>/g, '<div class="table-container"><table>');
    html = html.replace(/<\/table>/g, '</table></div>');
    return html;
  };

  const toggleChat = () => {
    if (showChat) {
      if (hasQueried) {
        setShowReview(true);
      } else {
        setShowChat(false);
      }
    } else {
      setShowChat(true);
    }
  };

  const handleCloseReview = () => {
    setShowReview(false);
  };

  const handleReviewSubmit = async () => {
    if (rating !== null) {
      const reviewPayload = {
        RatingID: rating,
        Options: feedbackOptions.join(','),
        Comments: comment
      };

      try {
        const response = await apiService.postData('review', reviewPayload);
        console.log('Review submitted:', reviewPayload);
      } catch (error) {
        console.error('Error submitting review:', error);
      }

      setMessages([{ from: 'bot', text: 'Hello! How can I assist you today?', id: Date.now() }]);
      setShowReview(false);
      setShowChat(false);
      setHasQueried(false);
      setRating(null);
      setComment('');
      setFeedbackOptions([]);
      stopMessage();
    }
  };

  const handleSkipReview = () => {
    setMessages([{ from: 'bot', text: 'Hello! How can I assist you today?', id: Date.now() }]);
    setShowReview(false);
    setShowChat(false);
    setHasQueried(false);
    setRating(null);
    setComment('');
    setFeedbackOptions([]);
    stopMessage();
  };

  const handleInputChange = (e) => {
    setQuestion(e.target.value || '');
  };

  const handlePredefinedQuestion = async (q) => {
    if (typeof q !== 'string') {
      console.error('Invalid predefined question:', q);
      return;
    }
    setQuestion(q);
    await handleSend(q);
  };

  const handleSend = async (customQuestion) => {
    const currentQuestion = typeof customQuestion === 'string' ? customQuestion : question;
    if (typeof currentQuestion !== 'string' || !currentQuestion.trim()) {
      console.warn('Invalid or empty question:', currentQuestion);
      return;
    }

    setQuestion('');
    const userMessage = { from: 'user', text: currentQuestion, id: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setHasQueried(true);

    try {
  const payload = { question: currentQuestion };
  const response = await apiService.postData('chat', payload);

  // Handle successful response
  const botMessage = {
    from: 'bot',
    text: response?.message?.answer || 'Sorry, I could not understand the response.',
    id: Date.now()
  };
  setMessages((prev) => [...prev, botMessage]);
} catch (error) {
  console.error('Error:', error);

  let errorText = 'Sorry, there was an error.'; // Default error message

  // Check if the error is a response with a 429 status code
  if (error.response && error.response.status === 429) {
    errorText = error.response.data.message || errorText; // Use server's custom message
  } else if (error.response && error.response.data && error.response.data.message) {
    errorText = error.response.data.message; // Handle other server errors with a message
  }

  const errorMessage = { from: 'bot', text: errorText, id: Date.now() };
  setMessages((prev) => [...prev, errorMessage]);
} finally {
  setLoading(false);
}
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleOptionToggle = (value) => {
    setFeedbackOptions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <div className="chatbot-icon" onClick={toggleChat}>
        <img src={chatIconUrl} alt="Chat Icon" className="chat-icon-img" />
      </div>

      {showChat && (
        <div className={`chat-window ${isExpanded ? 'expanded' : ''}`}>
          <div className="chat-window-header">
            CSAT Assistant
            <div className="header-buttons">
              <button
                className="expand-button"
                onClick={toggleExpand}
                title={isExpanded ? 'Collapse' : 'Expand'}
                aria-label={isExpanded ? 'Collapse chat window' : 'Expand chat window'}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isExpanded ? (
                    // Collapse icon (inward corners)
                    <>
                      <path d="M9 3v2H5v4H3V5a2 2 0 0 1 2-2h4z" />
                      <path d="M15 3v2h4v4h2V5a2 2 0 0 0-2-2h-4z" />
                      <path d="M9 21v-2H5v-4H3v4a2 2 0 0 0 2 2h4z" />
                      <path d="M15 21v-2h4v-4h2v4a2 2 0 0 1-2 2h-4z" />
                    </>
                  ) : (
                    // Expand icon (outward corners)
                    <>
                      <path d="M9 3H5a2 2 0 0 0-2 2v4" />
                      <path d="M15 3h4a2 2 0 0 1 2 2v4" />
                      <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
                      <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
                    </>
                  )}
                </svg>
              </button>
              <button
                className="close-button"
                onClick={toggleChat}
                title="Close"
                aria-label="Close chat window"
              >
                ×
              </button>
            </div>
          </div>
          <div className="chat-window-body">
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-message ${msg.from === 'user' ? 'user' : 'bot'} slide-in`}>
                {msg.from === 'user' ? (
                  msg.text
                ) : (
                  <div className="bot-message-container">
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                    <div className="audio-controls">
                      <button
                        className="play-pause-button"
                        onClick={() => togglePlayPause(msg.text, msg.id)}
                        title={playingMessageId === msg.id && !isPaused ? 'Pause audio' : 'Play audio'}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          width="20"
                          height="20"
                        >
                          {playingMessageId === msg.id && !isPaused ? (
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                          ) : (
                            <path d="M8 5v14l11-7z" />
                          )}
                        </svg>
                      </button>
                      <button
                        className="stop-button"
                        onClick={stopMessage}
                        disabled={playingMessageId !== msg.id}
                        title="Stop audio"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          width="20"
                          height="20"
                        >
                          <path d="M6 6h12v12H6z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="chat-message bot typing-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="chat-window-footer">
            <div className="predefined-questions">
              {predefinedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  className="predefined-question"
                  onClick={() => handlePredefinedQuestion(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="input-container">
              <input
                type="text"
                value={question}
                onChange={handleInputChange}
                placeholder="Type your question..."
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleSend()}
                disabled={loading || isRecording}
              />
              <button
                className={`mic-icon ${isRecording ? 'recording' : ''}`}
                onClick={toggleRecording}
                disabled={loading}
                title={isRecording ? 'Stop recording' : 'Start recording'}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="24"
                  height="24"
                >
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.47 6 6.93V21h2v-2.07c3.39-.46 6-3.4 6-6.93h-2z" />
                </svg>
              </button>
              <button
                className="send-icon"
                onClick={handleSend}
                disabled={!question.trim() || loading || isRecording}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="24"
                  height="24"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
          {showReview && (
            <div className="review-overlay">
              <div className="review-modal">
                <button className="close-review" onClick={handleCloseReview}>
                  ×
                </button>
                <h3>Was I Helpful?</h3>
                <p>Your feedback matters!</p>
                <div className="rating-container">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      className={`rating-button ${rating === value ? 'selected' : ''}`}
                      onClick={() => setRating(value)}
                    >
                      {value === 1 && '😞'}
                      {value === 2 && '😕'}
                      {value === 3 && '😐'}
                      {value === 4 && '😊'}
                      {value === 5 && '😄'}
                    </button>
                  ))}
                </div>
                {(rating === 1 || rating === 2 || rating === 3) && (
                  <div className="feedback-options">
                    <p>What could have been better?</p>
                    <label>
                      <input
                        type="checkbox"
                        value="not_satisfied"
                        onChange={() => handleOptionToggle('not_satisfied')}
                      />
                      Not satisfied with response
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="higher_time"
                        onChange={() => handleOptionToggle('higher_time')}
                      />
                      Higher response time
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="not_resolved"
                        onChange={() => handleOptionToggle('not_resolved')}
                      />
                      Query was not resolved even partially
                    </label>
                  </div>
                )}
                <div className="comment-section">
                  <p>Leave a comment</p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Type your comment here..."
                  />
                </div>
                <p className="clear-history-note">Submitting this review will clear the chat history.</p>
                <div className="review-actions">
                  <button
                    className="submit-review"
                    onClick={handleReviewSubmit}
                    disabled={rating === null || (rating <= 3 && feedbackOptions.length === 0)}
                  >
                    Submit
                  </button>
                  <button className="skip-review" onClick={handleSkipReview}>
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatBot;